import {ReactNode, useRef, useState} from "react";
import {useDrag} from "react-dnd";
import {useIntegrator} from "../../integrator.context";
import {useApp} from "../../app.context";
import PaletteTabButton from "../../elements/paletteTabButton";
import AssetImage from "../../elements/assetImage.element";
import Collapsible from "../../elements/collapsible";
import BooleanInput from "../../elements/booleanInput.element";
import {computeMapMetadata, MapDocument, MAP_SIZE_MAX, MAP_SIZE_MIN, MAP_SIZE_PRESETS} from "../../models/map.model";
import {ImageOverrides, packImageKeys, resolveSelectedImageKeys, toggleSelection} from "./mapImagePrefs";
import {MAP_PALETTE_DRAG, MapPaletteDragItem} from "./mapCanvas";

// Bright-blue outline marking the variant that will be painted — deliberately
// distinct from the row-selection tint so "which entity" and "which variant"
// read separately. Shown only on the SELECTED row: an unselected entity's brush
// isn't in play, so outlining it just competes for attention.
const VARIANT_OUTLINE = '#1e90ff';

// The Map Editor's right-hand panel: pick tiles/resources to paint with,
// review spawns, and edit map properties (name, translations, size) with a
// live metadata readout and the authoring warnings list.

interface MapPaletteProps {
    doc: MapDocument;
    // Tab selection is owned by the editor view so the canvas toolbar can
    // drive it (Paint/Fill → Tiles, Resource → Resources).
    tab: string;
    onSetTab(tab: string): void;
    selectedTileId?: string;
    selectedResourceId?: string;
    warnings: string[];
    // Another listed map already has this English name, which blocks publishing.
    nameClash: boolean;
    onSelectTile(id: string): void;
    onSelectResource(id: string): void;
    // The eraser row: the resource brush clears cells rather than filling them.
    // Mutually exclusive with a selected resource, resolved by the view.
    eraseResources: boolean;
    onSetEraseResources(erase: boolean): void;
    // The per-entity variant brush (see mapImagePrefs) — which pack images painting
    // may embed. Rows show every variant inline and outline the selected ones; paint
    // and fill pick among them per cell, so a stroke can scatter variants. Every paint
    // pins a concrete image, so a saved map looks identical to anyone who loads it.
    imageOverrides: ImageOverrides;
    onSetEntityImages(entityId: string, imageKeys: string[]): void;
    // Non-undoable property edits (name, display names) — applied directly.
    onUpdateDoc(mutate: (doc: MapDocument) => void): void;
    // Size changes crop/pad the grid — confirmed and undo-recorded upstream.
    onResize(newSize: number): void;
    // Canvas grid (view preferences): on/off + bold-line cadence (2–40).
    gridSize: number;
    onSetGridSize(size: number): void;
    // Only the BOLD cadence — the per-cell grid is always drawn.
    boldGridEnabled: boolean;
    onSetBoldGridEnabled(enabled: boolean): void;
    // Live state for THIS map. liveKnown false means the live manifest could not
    // be read, which must render as "unknown" rather than "not live" — publishing
    // writes to dev only, so the difference is the whole point of this section.
    liveKnown: boolean;
    liveVersion?: number;
    canPromote: boolean;
    onPromote(): void;
    onWithdraw(): void;
}

function MapPalette(props: MapPaletteProps) {
    const {state} = useIntegrator();
    const {getLocalizationValue} = useApp();
    // The Spawns tab was folded into Map — map a stale persisted selection.
    const effectiveTab = props.tab === 'Spawns' ? 'Map' : props.tab;
    const [namesOpen, setNamesOpen] = useState(false);

    const doc = props.doc;
    const translationLanguages = Object.keys(state.localization ?? {}).filter(lang => lang !== 'en');
    const translationCount = translationLanguages.filter(lang => doc.displayNames[lang]).length;
    const metadata = computeMapMetadata(doc, state.tiles);

    const activeTiles = [...state.tiles].filter(t => t.active)
        .sort((a, b) => getLocalizationValue(a.id).localeCompare(getLocalizationValue(b.id)));
    const activeResources = [...state.resources].filter(r => r.active)
        .sort((a, b) => getLocalizationValue(a.id).localeCompare(getLocalizationValue(b.id)));

    // Pairwise Manhattan spawn distances below the client's minimum
    // (size / 2) — soft authoring hints, never blocking.
    const minSpawnDistance = Math.floor(doc.size / 2);
    const closePairs: {a: number; b: number; distance: number}[] = [];
    for (let i = 0; i < doc.spawns.length; i++) {
        for (let j = i + 1; j < doc.spawns.length; j++) {
            const distance = Math.abs(doc.spawns[i].x - doc.spawns[j].x)
                + Math.abs(doc.spawns[i].y - doc.spawns[j].y);
            if (distance < minSpawnDistance) closePairs.push({a: i, b: j, distance});
        }
    }

    function entityRow(id: string, kind: 'tile' | 'resource', selected: boolean, onSelect: () => void) {
        return <EntityRow key={id}
                          name={getLocalizationValue(id)}
                          entityId={id}
                          kind={kind}
                          selected={selected}
                          variants={packImageKeys(state, id)}
                          selectedVariants={resolveSelectedImageKeys(state, props.imageOverrides, id)}
                          onSelect={onSelect}
                          onToggleVariant={(key, additive) => {
                              props.onSetEntityImages(
                                  id,
                                  toggleSelection(resolveSelectedImageKeys(state, props.imageOverrides, id), key, additive));
                              onSelect();
                          }}/>;
    }

    return (
        <div className={'layout vertical gap box'} style={{height: 0, flexGrow: 1, overflowY: 'auto'}}>
            <div className={'layout horizontal gap'} style={{width: '100%'}}>
                <PaletteTabButton label={'Tiles'} current={effectiveTab} onClick={props.onSetTab}/>
                <PaletteTabButton label={'Resources'} current={effectiveTab} onClick={props.onSetTab}/>
                <PaletteTabButton label={'Map'} current={effectiveTab} onClick={props.onSetTab}/>
            </div>

            <div className={'layout vertical gap'} style={{flexGrow: 1, overflowY: 'auto'}}>
                {effectiveTab === 'Tiles' && <>
                    {/* Plain `layout vertical` (no gap class): rows sit flush. */}
                    <div className={'layout vertical'}>
                        {activeTiles.map(tile =>
                            entityRow(tile.id, 'tile', tile.id === props.selectedTileId, () => props.onSelectTile(tile.id)))}
                    </div>
                    <HelpText>
                        Click an image variant to paint with it — the map embeds it, so it looks the same
                        for everyone. <strong>Ctrl/Cmd+click</strong> adds or removes variants: with several
                        selected, paint and fill scatter them randomly across the cells they cover. Drag a
                        variant straight onto the map to place that one on a single cell. When there are more
                        variants than fit, drag the strip from the space between them to scroll it. Alt+click
                        the canvas picks up the tile and variant under the cursor.
                    </HelpText>
                </>}

                {effectiveTab === 'Resources' && <>
                    <div className={'layout vertical'}>
                        {/* Above the list, because it competes with the entries below it
                            rather than with the tools: the eraser is a brush that paints
                            nothing. Tiles need no equivalent — every cell always holds
                            one, so painting over IS erasing. */}
                        <div onClick={() => props.onSetEraseResources(!props.eraseResources)}
                             title={'Paint over resources to remove them'}
                             className={'layout horizontal'}
                             style={{
                                 gap: 8, padding: 4, borderRadius: 4, cursor: 'pointer',
                                 userSelect: 'none', alignItems: 'center',
                                 backgroundColor: props.eraseResources ? 'rgba(255,255,255,0.12)' : undefined,
                             }}>
                            <div className={'layout center'}
                                 style={{
                                     width: 40, height: 40, flexShrink: 0, borderRadius: 5,
                                     border: `2px solid ${props.eraseResources ? VARIANT_OUTLINE : 'rgba(255,255,255,0.25)'}`,
                                 }}>
                                {/* Explicit colour: the row inherits no text colour here,
                                    so the glyph rendered black against the dark panel.
                                    1.3em is 30% up on the inherited size. */}
                                <span className={'fa-solid fa-eraser'}
                                      style={{color: 'white', fontSize: '1.3em'}}/>
                            </div>
                            <p style={{
                                flexShrink: 0, marginLeft: 'auto', textAlign: 'right',
                                color: props.eraseResources ? 'var(--tint-color)' : undefined,
                            }}>
                                Eraser
                            </p>
                        </div>
                        {activeResources.map(resource =>
                            entityRow(resource.id, 'resource',
                                !props.eraseResources && resource.id === props.selectedResourceId,
                                () => props.onSelectResource(resource.id)))}
                    </div>
                    <HelpText>
                        Click an image variant to paint with it, then drag across the map like the tile
                        brush. <strong>Ctrl/Cmd+click</strong> adds or removes variants: with several
                        selected, a stroke scatters them randomly. Resources can be painted onto any tile,
                        including ones that don't allow them — the Map tab warns about those rather than
                        stopping you. Pick <strong>Eraser</strong> to paint resources away. Dragging a variant
                        onto the map places that one on a single cell. When there are more variants than fit,
                        drag the strip from the space between them to scroll it.
                    </HelpText>
                </>}

                {effectiveTab === 'Map' && <>
                    {/* The English display name IS the map's name. The storage id
                        games pin is minted from it on first publish and never
                        authored, so it is only shown, read-only, once it exists. */}
                    <p className={'text header'}>Name</p>
                    <div className={'layout vertical'} style={{gap: 4}}>
                        <input value={doc.displayNames['en'] ?? ''}
                               placeholder={'map name...'}
                               onChange={e => props.onUpdateDoc(d => {
                                   if (e.target.value.length > 0) d.displayNames['en'] = e.target.value;
                                   else delete d.displayNames['en'];
                               })}
                               style={props.nameClash ? {borderColor: '#a03030'} : undefined}/>
                        {props.nameClash &&
                            <p className={'warning-text'} style={{fontSize: 12}}>
                                Another map already has this name.
                            </p>}
                    </div>
                    {translationLanguages.length > 0 &&
                    <Collapsible open={namesOpen} onOpenChange={setNamesOpen}
                                 title={<p style={{cursor: 'pointer'}} onClick={() => setNamesOpen(!namesOpen)}>
                                     {'Translations'}
                                     <span className={'muted-text'}>
                                         {` — ${translationCount > 0 ? `${translationCount} of ${translationLanguages.length}` : 'optional'}`}
                                     </span>
                                 </p>}>
                        <div className={'layout vertical gap'} style={{paddingLeft: 18, paddingTop: 6}}>
                            {translationLanguages.map(lang => (
                                <div key={lang} className={'layout vertical'} style={{gap: 4}}>
                                    <p className={'muted-text'} style={{fontSize: 12}}>{`Name (${lang})`}</p>
                                    <input value={doc.displayNames[lang] ?? ''}
                                           placeholder={`name in ${lang}...`}
                                           onChange={e => props.onUpdateDoc(d => {
                                               if (e.target.value.length > 0) d.displayNames[lang] = e.target.value;
                                               else delete d.displayNames[lang];
                                           })}/>
                                </div>
                            ))}
                        </div>
                    </Collapsible>}

                    <p className={'text header'}>Map Size</p>
                    <div className={'layout horizontal gap'} style={{flexWrap: 'wrap'}}>
                        {MAP_SIZE_PRESETS.map(size => (
                            <button key={size}
                                    onClick={() => size !== doc.size && props.onResize(size)}
                                    style={doc.size === size ? {color: 'var(--tint-color)'} : undefined}>
                                {`${size}×${size}`}
                            </button>
                        ))}
                        <input type={'number'} min={MAP_SIZE_MIN} max={MAP_SIZE_MAX}
                               defaultValue={doc.size} key={doc.size}
                               style={{width: 64}}
                               title={`Custom size (${MAP_SIZE_MIN}–${MAP_SIZE_MAX})`}
                               onBlur={e => {
                                   const size = Math.round(Number(e.target.value));
                                   if (Number.isFinite(size) && size >= MAP_SIZE_MIN && size <= MAP_SIZE_MAX && size !== doc.size)
                                       props.onResize(size);
                               }}/>
                    </div>

                    <p className={'text header'}>Grid Size</p>
                    <div className={'layout horizontal gap'} style={{alignItems: 'center'}}>
                        <BooleanInput value={props.boldGridEnabled} onChange={props.onSetBoldGridEnabled}/>
                        <p className={'muted-text'}>Show grid</p>
                    </div>
                    <div className={'layout horizontal gap'} style={{alignItems: 'center', opacity: props.boldGridEnabled ? 1 : 0.5}}>
                        {/* Value ahead of the slider, in full white: it's the readout
                            you check while dragging, so it shouldn't be muted or
                            trailing. Wide enough for the two-digit "40x40". */}
                        <p style={{width: 48, color: 'white'}}>{`${props.gridSize}x${props.gridSize}`}</p>
                        <input type={'range'} min={2} max={40} step={1}
                               value={props.gridSize}
                               disabled={!props.boldGridEnabled}
                               onChange={e => props.onSetGridSize(Number(e.target.value))}
                               style={{flexGrow: 1}}/>
                    </div>

                    <p className={'text header'}>Metadata</p>
                    <p className={'muted-text'}>{`Size: ${doc.size}×${doc.size} (${doc.size * doc.size} tiles)`}</p>
                    <p className={'muted-text'}>{`Water: ${(metadata.waterFraction * 100).toFixed(1)}%`}</p>
                    <p className={'muted-text'}>{`Impassable: ${(metadata.impassableFraction * 100).toFixed(1)}%`}</p>

                    <p className={'muted-text'}>{`Spawns: ${metadata.spawnCount}`}</p>
                    <div className={'layout horizontal gap'} style={{alignItems: 'center'}}>
                        <BooleanInput value={props.doc.spawnRandom === true}
                                      onChange={value => props.onUpdateDoc(d => {
                                          // Only written when set: absent is the default
                                          // everywhere, and it keeps schemaVersion at 1.
                                          if (value) d.spawnRandom = true;
                                          else delete d.spawnRandom;
                                      })}/>
                        <p className={'muted-text'}>Spawn random</p>
                    </div>
                    {doc.spawns.map((spawn, index) => (
                        <p key={`${spawn.x},${spawn.y}`} className={'muted-text'} style={{paddingLeft: 12}}>
                            {`${index + 1} — (${spawn.x}, ${spawn.y})`}
                        </p>
                    ))}
                    {closePairs.map(pair => (
                        <p key={`${pair.a}-${pair.b}`} className={'warning-text'} style={{paddingLeft: 12}}>
                            {`Spawns ${pair.a + 1} and ${pair.b + 1} are only ${pair.distance} apart (recommended ≥ ${minSpawnDistance}) — affects games with ${Math.max(pair.a, pair.b) + 1}+ players`}
                        </p>
                    ))}
                    {doc.versionNumber > 0 && <>
                        <p className={'muted-text'}>{`Published to dev: v${doc.versionNumber}`}</p>
                        <p className={'muted-text'} style={{userSelect: 'text'}}
                           title={'The id games and matchmaking pools refer to this map by. Fixed at first publish.'}>
                            {`ID: ${doc.name}`}
                        </p>
                    </>}
                    {doc.authoredAgainst &&
                        <p className={'muted-text'}>{`Authored against: ${doc.authoredAgainst.branch} v${doc.authoredAgainst.versionNumber}`}</p>}

                    {props.warnings.length > 0 && <>
                        <p className={'text header'}>Warnings</p>
                        {props.warnings.map((warning, index) =>
                            <p key={index} className={'warning-text'} style={{fontSize: 12}}>{warning}</p>)}
                    </>}

                    <HelpText>
                        Toggle spawn cities with the <strong>Spawn</strong> tool (toolbar above the canvas).
                        Spawn numbers are <strong>assignment priority</strong>: a game with N players always
                        uses spawns 1–N in order, so arrange them so every prefix is a fair start (e.g. 1 and 2
                        diametrically opposite for 2-player games). Keep starts {minSpawnDistance}+ apart (size / 2).
                    </HelpText>

                    {/* Live status and promotion. Publishing writes a new version to
                        the DEV backend only; a map reaches players exclusively by
                        being promoted, which is why this states the live version
                        outright rather than leaving it to be inferred. */}
                    <p className={'text header'}>Live</p>
                    <p className={'muted-text'} style={{fontSize: 12}}>
                        Publishing makes a version playable in the <strong>dev client</strong>. Promoting is what
                        puts it in front of <strong>live players</strong>.
                    </p>
                    {doc.versionNumber <= 0
                        ? <p className={'muted-text'}>Publish this map before promoting it.</p>
                        : !props.liveKnown
                            ? <p className={'warning-text'} style={{fontSize: 12}}>
                                Couldn't read the live map list — live status unknown.
                            </p>
                            : props.liveVersion === undefined
                                ? <p className={'muted-text'}>Dev only — live players can't see this map.</p>
                                : props.liveVersion === doc.versionNumber
                                    ? <p style={{color: 'var(--tint-color)'}}>{`Live — live players have v${props.liveVersion}.`}</p>
                                    : <p className={'warning-text'}>
                                        {`Live players have v${props.liveVersion}; this is v${doc.versionNumber}.`}
                                    </p>}

                    {props.canPromote && doc.versionNumber > 0 &&
                        <div className={'layout horizontal'} style={{gap: 8}}>
                            <button disabled={props.liveVersion === doc.versionNumber}
                                    style={{opacity: props.liveVersion === doc.versionNumber ? 0.5 : 1}}
                                    title={props.liveVersion === doc.versionNumber
                                        ? 'This version is already live'
                                        : `Put v${doc.versionNumber} in front of live players`}
                                    onClick={props.onPromote}>
                                Promote to Live
                            </button>
                            <button disabled={props.liveVersion === undefined}
                                    style={{opacity: props.liveVersion === undefined ? 0.5 : 1}}
                                    title={'Remove this map from the live map list'}
                                    onClick={props.onWithdraw}>
                                Withdraw
                            </button>
                        </div>}
                    {!props.canPromote &&
                        <p className={'muted-text'} style={{fontSize: 12}}>Only Admins can promote maps to live.</p>}
                </>}
            </div>

        </div>
    );
}

// One palette row: every pack variant inline on the left (the selected variants get a
// bright-blue outline; clicking one selects the entity AND sets its brush), localized
// name right-aligned. When the variants overflow, the
// strip drag-scrolls (no scrollbar) — a drag past the slop suppresses the
// click that follows so scrolling never changes the selection.
function EntityRow(props: {
    name: string;
    entityId: string;
    kind: 'tile' | 'resource';
    selected: boolean;
    variants: string[];
    selectedVariants: string[];
    onSelect(): void;
    onToggleVariant(imageKey: string, additive: boolean): void;
}) {
    const stripRef = useRef<HTMLDivElement>(null);
    const dragRef = useRef<{startX: number; startScroll: number} | undefined>(undefined);
    const draggedRef = useRef(false);

    function onStripPointerDown(e: React.PointerEvent<HTMLDivElement>) {
        if (e.button !== 0 || !stripRef.current) return;
        // Only from the space between (or after) the swatches. A press ON a swatch
        // begins a drag onto the map instead, and starting a scroll here would both
        // fight it and take the pointer capture the drag needs. The strip is the
        // event target only for its own gaps and trailing space.
        if (e.target !== e.currentTarget) return;
        dragRef.current = {startX: e.clientX, startScroll: stripRef.current.scrollLeft};
        draggedRef.current = false;
        // Deliberately NO pointer capture yet: a captured pointer retargets
        // the following click to the capturing element, which would swallow
        // clicks on the variant children. Capture starts with the drag.
    }

    function onStripPointerMove(e: React.PointerEvent<HTMLDivElement>) {
        const drag = dragRef.current;
        if (!drag || !stripRef.current) return;
        const dx = e.clientX - drag.startX;
        if (!draggedRef.current && Math.abs(dx) <= 4) return;   // click slop
        if (!draggedRef.current) {
            draggedRef.current = true;
            stripRef.current.setPointerCapture(e.pointerId);
        }
        stripRef.current.scrollLeft = drag.startScroll - dx;
    }

    function onStripPointerEnd() {
        dragRef.current = undefined;
    }

    // Swallow the click that ends a drag (capture phase, so neither the
    // variant's onClick nor the row's onSelect fires).
    function onStripClickCapture(e: React.MouseEvent) {
        if (!draggedRef.current) return;
        draggedRef.current = false;
        e.preventDefault();
        e.stopPropagation();
    }

    return (
        <div onClick={props.onSelect}
             // Plain `layout horizontal` — the house `center` class centers
             // the MAIN axis (justify-content), which would center the row.
             className={'layout horizontal'}
             // Selection reads from the background tint (and the name color)
             // alone — no border, and rows stack flush with no gap.
             style={{
                 gap: 8, padding: 4, borderRadius: 4, cursor: 'pointer', userSelect: 'none',
                 alignItems: 'center',
                 backgroundColor: props.selected ? 'rgba(255,255,255,0.12)' : undefined,
             }}>
            <div ref={stripRef}
                 onPointerDown={onStripPointerDown}
                 onPointerMove={onStripPointerMove}
                 onPointerUp={onStripPointerEnd}
                 onPointerCancel={onStripPointerEnd}
                 onClickCapture={onStripClickCapture}
                 className={'layout horizontal'}
                 // BOTH axes hidden: no scrollbars at all — the strip pans via
                 // drag (scrollLeft driven from the pointer handlers). Hiding
                 // only overflow-x would compute overflow-y to auto (CSS rule:
                 // one hidden axis promotes the other from visible), which
                 // grew a 1px-scrollable vertical scrollbar off the outline
                 // borders.
                 style={{gap: 4, flexGrow: 1, minWidth: 0, overflow: 'hidden', touchAction: 'none', alignItems: 'center'}}>
                {props.variants.length === 0 &&
                    <div title={'No images resolve for this entity in the current branch'}
                         style={{
                             width: 40, height: 40, borderRadius: 5, flexShrink: 0,
                             border: '1px dashed rgba(255,255,255,0.4)',
                         }}/>}
                {props.variants.map(key => (
                    <VariantSwatch key={key}
                                   imageKey={key}
                                   entityId={props.entityId}
                                   kind={props.kind}
                                   // Only the selected entity shows which of its
                                   // variants is the brush: on every other row the
                                   // outline marks a choice that isn't in play, and
                                   // several of them at once read as competing
                                   // selections.
                                   current={props.selected && props.selectedVariants.includes(key)}
                                   onToggle={additive => props.onToggleVariant(key, additive)}/>
                ))}
            </div>
            <p style={{
                flexShrink: 0, marginLeft: 'auto', textAlign: 'right',
                color: props.selected ? 'var(--tint-color)' : undefined,
            }}>
                {props.name}
            </p>
        </div>
    );
}

// Tab help, pinned below the list it describes. Above the list its height changed
// with the panel width — the text rewraps as you drag the splitter — and every row
// under it shifted up and down with it. A rule separates it from the controls.
function HelpText(props: {children: ReactNode}) {
    return (
        <p className={'muted-text'}
           style={{
               borderTop: '1px solid rgba(255,255,255,0.12)',
               paddingTop: 8, marginTop: 4, fontSize: 12,
           }}>
            {props.children}
        </p>
    );
}

// One pack variant. Click makes it the brush; dragging it onto the canvas places that
// exact entity and image on the dropped cell, whatever tool is active.
//
// Its own component because each swatch needs its own useDrag, and hooks can't be
// called from a loop.
function VariantSwatch(props: {
    imageKey: string;
    entityId: string;
    kind: 'tile' | 'resource';
    current: boolean;
    onToggle(additive: boolean): void;
}) {
    const item: MapPaletteDragItem = {kind: props.kind, id: props.entityId, image: props.imageKey};
    const [{isDragging}, drag] = useDrag({
        type: MAP_PALETTE_DRAG,
        item,
        collect: monitor => ({isDragging: monitor.isDragging()}),
    });

    return (
        <div ref={drag}
             title={props.imageKey}
             // stopPropagation so selecting a variant doesn't also re-fire the row's
             // own onSelect. A completed drag emits no click at all, so this can't
             // fire as a side effect of dragging onto the map.
             // Ctrl/Cmd toggles this variant in or out of the selection; a plain click
             // replaces the selection with just this one.
             onClick={e => {
                 e.stopPropagation();
                 props.onToggle(e.ctrlKey || e.metaKey);
             }}
             style={{
                 flexShrink: 0, borderRadius: 7, padding: 1, cursor: 'grab',
                 opacity: isDragging ? 0.4 : 1,
                 border: `2px solid ${props.current ? VARIANT_OUTLINE : 'transparent'}`,
             }}>
            <AssetImage id={props.imageKey} size={40}/>
        </div>
    );
}

export default MapPalette;
